export const useAboutPage = () => {
  //
  // Composables
  //
  const { t, te } = useI18n();
  const { fetchAllServiceVersions, clearCoreCache, loadingMap } = useService("aboutPage.service");

  //
  // State
  //
  const activeTab = useCookie<string>("aboutPage.activeTab", {
    default: () => "overview",
    maxAge: 60 * 60 * 24 * 365, // 1 year
  });

  const servicesData = useState<
    Array<{
      id: string;
      name: string;
      icon: string;
      infoUrl: string;
      healthUrl: string;
      appUrl?: string;
      openApiUrl?: string;
      version: string;
      description: string;
      healthStatus: string;
    }>
  >("aboutPage.servicesData", () => []);

  /** Overlay per-service descriptions with localised i18n strings when available. */
  const servicesDataLocalized = computed(() =>
    servicesData.value.map((svc) => ({
      ...svc,
      description: te(`pages.about.tabs.services_tab.${svc.id}_description`)
        ? t(`pages.about.tabs.services_tab.${svc.id}_description`)
        : svc.description,
    })),
  );

  //
  // Computed
  //
  const tabs = computed(() =>
    [
      ["overview", "mdi-information-outline"],
      ["services", "mdi-cloud-outline"],
      ["actions", "mdi-lightning-bolt"],
      ["copyright", "mdi-copyright"],
    ].map(([value, icon]) => ({
      value,
      icon,
      name: t(`pages.about.tabs.${value}_tab.name`),
    })),
  );

  const actionsData = computed(() => [
    {
      id: "clear-cache",
      icon: "mdi-refresh",
      iconColor: "error",
      title: t("pages.about.tabs.actions_tab.cache_card.title"),
      description: t("pages.about.tabs.actions_tab.cache_card.content"),
      buttonIcon: "mdi-refresh",
      buttonText: t("pages.about.tabs.actions_tab.cache_card.action"),
      buttonColor: "primary",
      loading: loadingMap.clearCoreCache.value,
    },
  ]);

  //
  // Methods
  //

  /**
   * Clear core cache.
   * @returns {Promise<boolean>} True if successful, false otherwise.
   */
  const refreshCoreCache = async (): Promise<boolean> => {
    loadingMap.clearCoreCache.value = true;

    try {
      const success = await clearCoreCache();
      return success;
    } catch (error) {
      console.error("Error refreshing core cache:", error);
      return false;
    } finally {
      loadingMap.clearCoreCache.value = false;
    }
  };

  /**
   * Fetch all service versions and health status
   */
  const fetchServiceVersions = async (): Promise<boolean> => {
    loadingMap.fetchAllServiceVersions.value = true;

    try {
      servicesData.value = await fetchAllServiceVersions();
      return true;
    } catch (err) {
      console.error("Error fetching service versions:", err);
      return false;
    } finally {
      loadingMap.fetchAllServiceVersions.value = false;
    }
  };

  return {
    // State
    activeTab,
    isCoreCacheRefreshing: readonly(loadingMap.clearCoreCache),
    isServicesRefreshing: readonly(loadingMap.fetchAllServiceVersions),

    // Computed
    tabs: readonly(tabs),
    servicesData: readonly(servicesDataLocalized),
    actionsData: readonly(actionsData),

    // Methods
    refreshCoreCache,
    fetchServiceVersions,
  };
};
